
import { getCurrentConference } from '../base/conference';
import { getBreakoutRooms, getMainRoom } from '../breakout-rooms/functions';
import { getVisitorsCount } from '../visitors/functions';
import { BACKSTAGE_ROOM_SUFFIX } from '../webinar/constants';
import { isBackstageModeEnabled } from '../webinar/functions';

/**
 * Replaces the localpart of a room JID while preserving its MUC domain.
 *
 * @param {string} roomJid - The source room JID.
 * @param {string} roomName - The new room localpart.
 * @returns {string|undefined}
 */
function _roomJidForName(roomJid: string, roomName?: string) {
    if (!roomJid || !roomName || !roomJid.includes('@')) {
        return undefined;
    }

    return `${roomName}@${roomJid.slice(roomJid.indexOf('@') + 1)}`;
}

/**
 * Whether the local participant is in a room that belongs to the meeting but is
 * not the meeting itself: its backstage room or one of its breakout rooms.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean}
 */
export function isInAssociatedRoom(state: Object): boolean {
    const conference = getCurrentConference(state);

    if (!conference) {
        return false;
    }

    if (conference.getBreakoutRooms?.()?.isBreakoutRoom?.()) {
        return true;
    }

    const roomName = conference.getName?.();

    return Boolean(roomName && roomName.endsWith(BACKSTAGE_ROOM_SUFFIX));
}

/**
 * Whether the meeting's lobby is on as far as the local moderator is concerned.
 *
 * {@code lobbyEnabled} mirrors the members-only flag of the room we are
 * currently in. Backstage and breakout rooms are never members-only, so it flips
 * to false the moment a moderator steps out of the main room - which used to
 * hide the knocking list and the participants pane lobby section exactly when
 * guests started knocking. In an associated room the authoritative signal is the
 * main room's lobby JID, which prosody advertises on that room's disco#info only
 * while the main room is members-only.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean}
 */
export function isLobbyEnabledForLocalModerator(state: Object): boolean {
    if (state['features/lobby'].lobbyEnabled) {
        return true;
    }

    if (!isInAssociatedRoom(state)) {
        return false;
    }

    return Boolean(getCurrentConference(state)?.room?.getLobby?.()?.lobbyRoomJid);
}

/**
 * Returns every room a moderator may choose when admitting lobby guests: the
 * meeting itself, its backstage room, and every breakout room that currently
 * exists. Usable from any of those rooms - the main room is resolved from the
 * room we are standing in, not assumed to be it.
 *
 * @param {Object} state - Redux state.
 * @returns {Array<Object>}
 */
export function getLobbyAdmissionRooms(state: Object): Array<Object> {
    const conference = getCurrentConference(state);
    const currentRoomJid = conference?.room?.roomjid;
    const currentRoomName = conference?.getName?.();
    const inBackstage = Boolean(currentRoomName && currentRoomName.endsWith(BACKSTAGE_ROOM_SUFFIX));
    let mainRoomJid;

    if (inBackstage) {
        // Backstage is '<meeting>-backstage' on the meeting's own MUC domain, so
        // dropping the suffix is exact - no server-published data needed.
        mainRoomJid = _roomJidForName(currentRoomJid, currentRoomName.slice(0, -BACKSTAGE_ROOM_SUFFIX.length));
    } else if (conference?.getBreakoutRooms?.()?.isBreakoutRoom?.()) {
        // A breakout room lives on its own MUC component and names its meeting
        // in disco#info; the broadcast room list is the fallback.
        mainRoomJid = conference.getBreakoutRooms().getMainRoomJid?.() || getMainRoom(state)?.jid;
    } else {
        mainRoomJid = currentRoomJid;
    }

    // The main room is always a destination - it is the room the guest knocked
    // on. When its JID cannot be resolved it is still offered with no JID:
    // omitting the destination on the wire means "the meeting", which is the
    // same thing.
    const rooms = [ {
        jid: mainRoomJid,
        key: 'main',
        kind: 'main',
        name: getMainRoom(state)?.name
    } ];
    const mainRoomName = mainRoomJid && mainRoomJid.includes('@')
        ? mainRoomJid.slice(0, mainRoomJid.indexOf('@'))
        : undefined;
    const backstageRoomJid = _roomJidForName(mainRoomJid, `${mainRoomName || ''}${BACKSTAGE_ROOM_SUFFIX}`);

    // Backstage is a meeting feature (ENABLE_BACKSTAGE_MODE in the token);
    // without it there is no backstage to admit anyone into.
    if (mainRoomName && backstageRoomJid && isBackstageModeEnabled(state)) {
        rooms.push({
            jid: backstageRoomJid,
            key: backstageRoomJid,
            kind: 'backstage'
        });
    }

    Object.values(getBreakoutRooms(state) || {})
        .filter((room: any) => room && !room.isMainRoom && room.jid)
        .forEach((room: any) => rooms.push({
            jid: room.jid,
            key: room.jid,
            kind: 'breakout',
            name: room.name
        }));

    return rooms.filter((room, index) => rooms.findIndex(candidate => candidate.key === room.key) === index);
}

/**
* Selector to return lobby enable state.
*
* @param {Object} state - State object.
* @returns {boolean}
*/
export function getLobbyEnabled(state: Object) {
    return state['features/lobby'].lobbyEnabled;
}

/**
* Selector to return a list of knocking participants.
*
* @param {Object} state - State object.
* @returns {Array<Object>}
*/
export function getKnockingParticipants(state: Object) {
    return state['features/lobby'].knockingParticipants;
}

/**
 * Approves (lets in) or rejects a knocking participant.
 *
 * @param {Function} getState - Function to get the Redux state.
 * @param {string} id - The id of the knocking participant.
 * @param {boolean} approved - True if the participant is approved, false otherwise.
 * @returns {Function}
 */
export function setKnockingParticipantApproval(getState: Function, id: string, approved: boolean) {
    const conference = getCurrentConference(getState());

    if (conference) {
        if (approved) {
            conference.lobbyApproveAccess(id);
        } else {
            conference.lobbyDenyAccess(id);
        }
    }
}

/**
 * Selector to return lobby state.
 *
 * @param {any} state - State object.
 * @returns {any}
 */
export function getLobbyState(state: any) {
    return state['features/lobby'];
}

/**
 * Selector to return lobby visibility.
 *
 * @param {IReduxState} state - State object.
 * @returns {any}
 */
export function getIsLobbyVisible(state: any) {
    return state['features/lobby'].lobbyVisible;
}

/**
 * Checks if lobby mode toggling is currently loading.
 * Replaces the old 5-second rate-limiting restriction with a check on the active
 * 2-3 second loader state so users can toggle frequently once the loader finishes.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Object} [state] - Optional Redux state object.
 * @returns {boolean} - True if currently loading, false otherwise.
 */
export function checkLobbyRateLimit(dispatch: Function, state?: Object): boolean {
    if (state && state['features/lobby']?.lobbyLoading) {
        return true;
    }

    if (typeof dispatch === 'function') {
        let isLoading = false;

        try {
            dispatch((_, getState) => {
                const s = typeof getState === 'function' ? getState() : undefined;

                if (s && s['features/lobby']?.lobbyLoading) {
                    isLoading = true;
                }
            });
        } catch {
            // ignore
        }

        if (isLoading) {
            return true;
        }
    }

    return false;
}

/**
 * Returns true if enabling lobby is allowed and false otherwise.
 *
 * @param {IReduxState} state - State object.
 * @returns {boolean}
 */
export function isEnablingLobbyAllowed(state) {
    return getVisitorsCount(state) <= 0;
}
